// src/pages/AdminPayments.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { adminFetch } from '../api/admin';
import { useToast } from '../components/ToastProvider';
import { LogOut, ArrowLeft, RefreshCw, Clock, CheckCircle, AlertCircle, XCircle, DollarSign, TrendingUp, Users, Package } from 'lucide-react';
import './AdminPayments.css';

const PERIOD_OPTIONS = [
    { value: 'today', label: 'Today' },
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: 'all', label: 'All Time' },
    { value: 'custom', label: 'Custom Range' }
];

export default function AdminPayments() {
    const [loading, setLoading] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const [error, setError] = useState('');
    const [data, setData] = useState(null);
    const [period, setPeriod] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const { showToast } = useToast();
    const navigate = useNavigate();

    // Check authentication on mount
    useEffect(() => {
        const checkAuth = async () => {
            setAuthLoading(true);
            try {
                const res = await adminFetch('admin-me.php');
                const data = await res.json();

                if (!data.success || !data.authenticated) {
                    navigate('/admin/login');
                } else {
                    setAuthLoading(false);
                }
            } catch (err) {
                navigate('/admin/login');
            }
        };

        checkAuth();
    }, [navigate]);

    const fetchAnalytics = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            let url = `payment-analytics.php?period=${period}`;
            if (period === 'custom' && startDate && endDate) {
                url = `payment-analytics.php?start_date=${startDate}&end_date=${endDate}`;
            }

            const res = await adminFetch(url);
            if (!res.ok) {
                const text = await res.text();
                throw new Error(`HTTP ${res.status}: ${text.substring(0, 100)}`);
            }

            const result = await res.json();
            if (!result.ok) {
                throw new Error(result.error || 'Failed to load analytics');
            }

            setData(result);
        } catch (err) {
            setError(err.message);
            showToast(err.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [period, startDate, endDate, showToast]);

    useEffect(() => {
        if (!authLoading) {
            fetchAnalytics();
        }
    }, [fetchAnalytics, authLoading]);

    const handleLogout = async () => {
        try {
            const res = await adminFetch('admin-logout.php', { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                showToast('Logged out successfully', 'success');
                navigate('/admin/login');
            }
        } catch (err) {
            navigate('/admin/login');
        }
    };

    const handlePeriodChange = (newPeriod) => {
        setPeriod(newPeriod);
        if (newPeriod !== 'custom') {
            setStartDate('');
            setEndDate('');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        try {
            return new Date(dateStr).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateStr;
        }
    };

    const getPaymentStatusBadge = (status) => {
        const statusMap = {
            'paid': { class: 'badge-success', icon: <CheckCircle size={12} /> },
            'pending': { class: 'badge-warning', icon: <Clock size={12} /> },
            'failed': { class: 'badge-danger', icon: <XCircle size={12} /> },
            'unknown': { class: 'badge-secondary', icon: <AlertCircle size={12} /> }
        };
        const config = statusMap[status] || statusMap['unknown'];
        return (
            <span className={`payment-badge ${config.class}`}>
                {config.icon} {status}
            </span>
        );
    };

    const stats = data?.stats || {};

    if (authLoading) {
        return (
            <div className="admin-payments-page">
                <div className="admin-loading">Checking authentication...</div>
            </div>
        );
    }

    return (
        <div className="admin-payments-page">
            {/* Header */}
            <div className="payments-header">
                <div className="payments-header-left">
                    <Link to="/admin/orders" className="back-link">
                        <ArrowLeft size={18} /> Back to Orders
                    </Link>
                    <h1>Payment Analytics</h1>
                </div>
                <button onClick={handleLogout} className="btn-logout">
                    <LogOut size={16} /> Logout
                </button>
            </div>

            {/* Period Filters */}
            <div className="payments-filters">
                <div className="period-buttons">
                    {PERIOD_OPTIONS.map(opt => (
                        <button
                            key={opt.value}
                            className={`period-btn ${period === opt.value ? 'active' : ''}`}
                            onClick={() => handlePeriodChange(opt.value)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>

                {period === 'custom' && (
                    <div className="custom-date-range">
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            placeholder="Start Date"
                        />
                        <span>to</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            placeholder="End Date"
                        />
                        <button
                            className="btn-apply"
                            onClick={fetchAnalytics}
                            disabled={!startDate || !endDate}
                        >
                            Apply
                        </button>
                    </div>
                )}

                <button className="btn-refresh" onClick={fetchAnalytics} disabled={loading}>
                    <RefreshCw size={16} className={loading ? 'spinning' : ''} />
                    {loading ? 'Loading...' : 'Refresh'}
                </button>
            </div>

            {error && <div className="payments-error">{error}</div>}

            {/* Stats Cards */}
            <div className="stats-grid">
                <div className="stat-card primary">
                    <div className="stat-icon"><DollarSign size={24} /></div>
                    <div className="stat-content">
                        <div className="stat-label">Total Revenue</div>
                        <div className="stat-value">{formatCurrency(stats.total_revenue || 0)}</div>
                        <div className="stat-sub">
                            Products: {formatCurrency(stats.product_revenue || 0)} | Shipping: {formatCurrency(stats.shipping_revenue || 0)}
                        </div>
                    </div>
                </div>

                <div className="stat-card success">
                    <div className="stat-icon"><CheckCircle size={24} /></div>
                    <div className="stat-content">
                        <div className="stat-label">Paid Orders</div>
                        <div className="stat-value">{stats.paid_orders || 0}</div>
                        <div className="stat-sub">Successful payments</div>
                    </div>
                </div>

                <div className="stat-card warning">
                    <div className="stat-icon"><Clock size={24} /></div>
                    <div className="stat-content">
                        <div className="stat-label">Pending Payments</div>
                        <div className="stat-value">{stats.pending_payments || 0}</div>
                        <div className="stat-sub">Abandoned checkouts</div>
                    </div>
                </div>

                <div className="stat-card info">
                    <div className="stat-icon"><TrendingUp size={24} /></div>
                    <div className="stat-content">
                        <div className="stat-label">Avg. Order Value</div>
                        <div className="stat-value">{formatCurrency(stats.average_order_value || 0)}</div>
                        <div className="stat-sub">Per paid order</div>
                    </div>
                </div>
            </div>

            {/* Period Breakdown - Always shown */}
            <div className="breakdown-section">
                <h2>Revenue Breakdown</h2>
                <div className="breakdown-grid">
                    <div className="breakdown-card">
                        <div className="breakdown-label">Today</div>
                        <div className="breakdown-value">{formatCurrency(stats.today_revenue || 0)}</div>
                    </div>
                    <div className="breakdown-card">
                        <div className="breakdown-label">Last 7 Days</div>
                        <div className="breakdown-value">{formatCurrency(stats.week_revenue || 0)}</div>
                    </div>
                    <div className="breakdown-card">
                        <div className="breakdown-label">Last 30 Days</div>
                        <div className="breakdown-value">{formatCurrency(stats.month_revenue || 0)}</div>
                    </div>
                    <div className="breakdown-card">
                        <div className="breakdown-label">Total Orders</div>
                        <div className="breakdown-value">{stats.total_orders || 0}</div>
                    </div>
                </div>
            </div>

            {/* Status Breakdown */}
            {stats.status_breakdown && Object.keys(stats.status_breakdown).length > 0 && (
                <div className="breakdown-section">
                    <h2>Order Status Distribution</h2>
                    <div className="breakdown-grid">
                        {Object.entries(stats.status_breakdown).map(([status, count]) => (
                            <div key={status} className={`breakdown-card status-${status}`}>
                                <div className="breakdown-label">{status.charAt(0).toUpperCase() + status.slice(1)}</div>
                                <div className="breakdown-value">{count}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Pending Orders Table */}
            {data?.pending_orders && data.pending_orders.length > 0 && (
                <div className="table-section">
                    <h2>
                        <AlertCircle size={20} /> Abandoned Checkouts
                        <span className="table-count">({data.pending_orders.length})</span>
                    </h2>
                    <p className="table-description">Orders where payment was initiated but not completed. Consider reaching out to these customers.</p>
                    <div className="table-responsive">
                        <table className="payments-table">
                            <thead>
                                <tr>
                                    <th>Order ID</th>
                                    <th>Customer</th>
                                    <th>Contact</th>
                                    <th>Amount</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.pending_orders.map(order => (
                                    <tr key={order.id}>
                                        <td><strong>#{order.id}</strong></td>
                                        <td>{order.customer_name}</td>
                                        <td>
                                            <div className="contact-info">
                                                <div>{order.email || '—'}</div>
                                                <div className="phone">{order.phone}</div>
                                            </div>
                                        </td>
                                        <td className="amount">{formatCurrency(order.total)}</td>
                                        <td className="date">{formatDate(order.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Recent Transactions Table */}
            {data?.recent_transactions && data.recent_transactions.length > 0 && (
                <div className="table-section">
                    <h2>
                        <Package size={20} /> Recent Transactions
                        <span className="table-count">({data.recent_transactions.length})</span>
                    </h2>
                    <div className="table-responsive">
                        <table className="payments-table">
                            <thead>
                                <tr>
                                    <th>Order ID</th>
                                    <th>Customer</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    <th>Payment</th>
                                    <th>Razorpay ID</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.recent_transactions.map(txn => (
                                    <tr key={txn.id}>
                                        <td><strong>#{txn.id}</strong></td>
                                        <td>{txn.customer_name}</td>
                                        <td className="amount">
                                            <div>{formatCurrency(txn.total)}</div>
                                            <div className="amount-breakdown">
                                                (₹{txn.subtotal} + ₹{txn.shipping})
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`order-status status-${txn.status}`}>
                                                {txn.status}
                                            </span>
                                        </td>
                                        <td>{getPaymentStatusBadge(txn.payment_status)}</td>
                                        <td className="razorpay-id">
                                            {txn.razorpay_order_id || '—'}
                                        </td>
                                        <td className="date">{formatDate(txn.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!loading && !data && !error && (
                <div className="payments-empty">No analytics data available.</div>
            )}
        </div>
    );
}
